import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import * as R from 'ramda';
import { Box, Button, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { RELEASE_NOTES_URL } from 'utils/constants';
import { useUserRolePermissions } from 'hooks';
import SwiftLogo from 'assets/img/dashboard-swift-logo.svg';
import MagellanLogo from 'assets/img/dashboard-magellan-logo.svg';
import { ReactComponent as InventoryIcon } from 'assets/img/inventory-icon.svg';
import { ReactComponent as LoadingIcon } from 'assets/img/loading-icon.svg';
import { ReactComponent as DispatchingIcon } from 'assets/img/dispatching-icon.svg';
import { ReactComponent as ShippingIcon } from 'assets/img/shipping-icon.svg';

// Components
import LanguageIcon from '@mui/icons-material/Language';
import { styled } from '@mui/material/styles';
import Flag from 'common/FeatureFlags/Flag';
import packageJson from '../../../package.json';
import ModuleButton from './ModuleButton';

const Logo = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: '1 0',
  justifyContent: 'center',
  marginTop: theme.spacing(3),
}));

const ModuleButtons = styled(Box)(() => ({
  display: 'flex',
  flexFlow: 'row wrap',
  justifyContent: 'center',
}));

const Root = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexFlow: 'column',
  justifyContent: 'space-between',
  padding: theme.spacing(7, 2, 6),
  [theme.breakpoints.up('sm')]: {
    height: '100vh',
  },
}));

const Dashboard = () => {
  const {
    currentAffiliate,
    isDispatchingView,
    isInventoryOwner,
    isProductLoadingView,
    isProductOwnerView,
    isProductShippingManage,
    isProductShippingView,
    isSupplier,
  } = useUserRolePermissions();

  // Disabled status are based on this document from magellan:
  // https://wiki.mmplp.net:8444/display/CASR/Swift+Screens+-+Affiliate+and+User+Permissions

  const showInventory = isInventoryOwner && isProductOwnerView;
  const showLoading = (isSupplier || isInventoryOwner) && isProductLoadingView;
  const showShipping = isInventoryOwner && (isProductShippingView || isProductShippingManage);

  const hasPermissions =
    isDispatchingView || isProductLoadingView || isProductOwnerView || isProductShippingView;

  const [dialogClosed, setDialogClosed] = useState(hasPermissions);

  const onClose = () => setDialogClosed(true);

  useEffect(() => {
    setDialogClosed(hasPermissions);
  }, [hasPermissions]);

  return (
    <>
      <Root component="main">
        <Logo className="heap-logo">
          <img alt="Swift" src={SwiftLogo} />
        </Logo>
        <ModuleButtons>
          <Flag value={process.env.REACT_APP_LD_INVENTORY}>
            <ModuleButton
              component={RouterLink}
              disabled={R.not(showInventory)}
              icon={<InventoryIcon />}
              loading={R.isNil(currentAffiliate)}
              to="inventory-management"
            >
              Inventory
            </ModuleButton>
          </Flag>
          <Flag value={process.env.REACT_APP_LD_SHIPPING}>
            <Box>
              <ModuleButton
                component={RouterLink}
                disabled={R.not(showShipping)}
                icon={<ShippingIcon />}
                loading={R.isNil(currentAffiliate)}
                to="shipping"
              >
                Shipping
              </ModuleButton>
            </Box>
          </Flag>
          <Flag value={process.env.REACT_APP_LD_LOADING}>
            <Box>
              <ModuleButton
                component={RouterLink}
                disabled={R.not(showLoading)}
                icon={<LoadingIcon />}
                loading={R.isNil(currentAffiliate)}
                to="loading-management"
              >
                Loading
              </ModuleButton>
            </Box>
          </Flag>
          <Flag value={process.env.REACT_APP_LD_DISPATCHING}>
            <Box>
              <ModuleButton
                component={RouterLink}
                disabled={R.not(isDispatchingView)}
                icon={<DispatchingIcon />}
                loading={R.isNil(currentAffiliate)}
                to="dispatching"
              >
                Dispatching
              </ModuleButton>
            </Box>
          </Flag>
          <Flag value={process.env.REACT_APP_LD_GLOBAL}>
            <Box>
              <ModuleButton
                component={RouterLink}
                icon={<LanguageIcon sx={{ fontSize: 120 }} />}
                loading={R.isNil(currentAffiliate)}
                to="global-data"
              >
                Global
              </ModuleButton>
            </Box>
          </Flag>
        </ModuleButtons>
        {!dialogClosed && (
          <Dialog
            maxWidth="xs"
            open
            slotProps={{
              paper: { elevation: 10 },
            }}
          >
            <DialogTitle>You don&apos;t have permissions to access this module.</DialogTitle>
            <DialogContent>
              Please contact your Local Security Admin to enable access to the grayed out module(s).
              <br />
              <br />
              If you have any questions or concerns contact our Commercial Technology Team.
            </DialogContent>
            <DialogActions>
              <Button autoFocus onClick={onClose} variant="outlined">
                Close
              </Button>
              <Button
                href="mailto:CommercialTechnology@magellanlp.com?subject=Re: Swift Access Assistance"
                rel="noopener noreferrer"
                sx={{ margin: '10px' }}
                target="_top"
              >
                Contact Us
              </Button>
            </DialogActions>
          </Dialog>
        )}
        <Box
          sx={{
            alignItems: 'flex-end',
            display: 'flex',
            flex: '1 0',
            justifyContent: 'center',
            marginTop: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Button
              className="heap-button"
              href={RELEASE_NOTES_URL}
              sx={{
                mb: 'auto',
                mt: 2,
                mx: 'auto',
                padding: '2px 0',
                width: 56,
              }}
              target="_blank"
              variant="text"
            >
              Release Notes
            </Button>
            <img
              alt="Magellan Midstream Partners, L.P."
              src={MagellanLogo}
              style={{ marginBottom: '24px' }}
            />
            <Typography align="center" sx={{ mb: 3 }} variant="caption">
              Version {packageJson.version}
            </Typography>
          </Box>
        </Box>
      </Root>
    </>
  );
};

export default Dashboard;
